<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const model = defineModel({ type: Object, required: true });

const repeatTypes = [
  { value: 'once', label: 'CAMPAIGN.V2.REPEAT_TYPES.ONCE' },
  { value: 'daily', label: 'CAMPAIGN.V2.REPEAT_TYPES.DAILY' },
  { value: 'weekly', label: 'CAMPAIGN.V2.REPEAT_TYPES.WEEKLY' },
  { value: 'monthly', label: 'CAMPAIGN.V2.REPEAT_TYPES.MONTHLY' },
  { value: 'custom', label: 'CAMPAIGN.V2.REPEAT_TYPES.CUSTOM' },
];

const daysOfWeek = [
  { value: 'Sunday', label: 'CAMPAIGN.V2.WIZARD.SCHEDULING.DAYS.SUNDAY' },
  { value: 'Monday', label: 'CAMPAIGN.V2.WIZARD.SCHEDULING.DAYS.MONDAY' },
  { value: 'Tuesday', label: 'CAMPAIGN.V2.WIZARD.SCHEDULING.DAYS.TUESDAY' },
  { value: 'Wednesday', label: 'CAMPAIGN.V2.WIZARD.SCHEDULING.DAYS.WEDNESDAY' },
  { value: 'Thursday', label: 'CAMPAIGN.V2.WIZARD.SCHEDULING.DAYS.THURSDAY' },
  { value: 'Friday', label: 'CAMPAIGN.V2.WIZARD.SCHEDULING.DAYS.FRIDAY' },
  { value: 'Saturday', label: 'CAMPAIGN.V2.WIZARD.SCHEDULING.DAYS.SATURDAY' },
];

const isWeekly = computed(() => model.value.repeatType === 'weekly');
const isMonthly = computed(() => model.value.repeatType === 'monthly');
const isCustom = computed(() => model.value.repeatType === 'custom');

const selectedDays = computed({
  get: () => model.value.repeatConfig?.days_of_week || [],
  set: val => {
    model.value = {
      ...model.value,
      repeatConfig: { ...model.value.repeatConfig, days_of_week: val },
    };
  },
});

const toggleDay = day => {
  const current = [...selectedDays.value];
  const idx = current.indexOf(day);
  if (idx >= 0) {
    current.splice(idx, 1);
  } else {
    current.push(day);
  }
  selectedDays.value = current;
};

const updateField = (field, value) => {
  model.value = { ...model.value, [field]: value };
};

const updateRepeatConfig = (key, value) => {
  model.value = {
    ...model.value,
    repeatConfig: { ...model.value.repeatConfig, [key]: value },
  };
};
</script>

<template>
  <div class="max-w-2xl space-y-6">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.TITLE') }}
    </h2>

    <!-- Title -->
    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.CAMPAIGN_TITLE_LABEL') }}
      </label>
      <input
        :value="model.title"
        type="text"
        class="w-full px-4 py-2 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 placeholder-n-slate-9 focus:border-n-brand focus:outline-none"
        :placeholder="t('CAMPAIGN.V2.WIZARD.SCHEDULING.CAMPAIGN_TITLE_PLACEHOLDER')"
        @input="updateField('title', $event.target.value)"
      />
    </div>

    <!-- Description -->
    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.DESCRIPTION_LABEL') }}
      </label>
      <textarea
        :value="model.description"
        class="w-full h-20 px-4 py-2 text-sm border rounded-lg resize-none border-n-weak bg-n-surface-1 text-n-slate-12 placeholder-n-slate-9 focus:border-n-brand focus:outline-none"
        :placeholder="t('CAMPAIGN.V2.WIZARD.SCHEDULING.DESCRIPTION_PLACEHOLDER')"
        @input="updateField('description', $event.target.value)"
      />
    </div>

    <!-- Repeat Type -->
    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.REPEAT_TYPE_LABEL') }}
      </label>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="rt in repeatTypes"
          :key="rt.value"
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
          :class="
            model.repeatType === rt.value
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
          "
          @click="updateField('repeatType', rt.value)"
        >
          {{ t(rt.label) }}
        </button>
      </div>
    </div>

    <!-- Weekly: Days of week -->
    <div v-if="isWeekly">
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.DAYS_OF_WEEK_LABEL') }}
      </label>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="day in daysOfWeek"
          :key="day.value"
          class="px-3 py-1.5 text-sm rounded-lg transition-colors"
          :class="
            selectedDays.includes(day.value)
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
          "
          @click="toggleDay(day.value)"
        >
          {{ t(day.label) }}
        </button>
      </div>
    </div>

    <!-- Monthly: Day of month -->
    <div v-if="isMonthly">
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.DAY_OF_MONTH_LABEL') }}
      </label>
      <input
        :value="model.repeatConfig?.day_of_month"
        type="number"
        min="1"
        max="31"
        class="w-32 px-4 py-2 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 focus:border-n-brand focus:outline-none"
        @input="updateRepeatConfig('day_of_month', parseInt($event.target.value))"
      />
    </div>

    <!-- Custom: Interval hours -->
    <div v-if="isCustom">
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.INTERVAL_HOURS_LABEL') }}
      </label>
      <input
        :value="model.repeatConfig?.interval_hours"
        type="number"
        min="1"
        class="w-32 px-4 py-2 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 focus:border-n-brand focus:outline-none"
        @input="updateRepeatConfig('interval_hours', parseInt($event.target.value))"
      />
    </div>

    <!-- Dispatch Time -->
    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.DISPATCH_TIME_LABEL') }}
      </label>
      <input
        :value="model.dispatchTime"
        type="time"
        class="w-40 px-4 py-2 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 focus:border-n-brand focus:outline-none"
        @input="updateField('dispatchTime', $event.target.value)"
      />
    </div>

    <!-- Scheduled At -->
    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.SCHEDULED_AT_LABEL') }}
      </label>
      <input
        :value="model.scheduledAt"
        type="datetime-local"
        class="w-64 px-4 py-2 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 focus:border-n-brand focus:outline-none"
        @input="updateField('scheduledAt', $event.target.value)"
      />
    </div>

    <!-- Allow Resend -->
    <div class="flex items-center gap-3">
      <input
        :checked="model.allowResend"
        type="checkbox"
        class="accent-n-brand"
        @change="updateField('allowResend', $event.target.checked)"
      />
      <label class="text-sm text-n-slate-12">
        {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.ALLOW_RESEND_LABEL') }}
      </label>
    </div>

    <!-- Send Limits -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.SEND_LIMIT_TOTAL_LABEL') }}
        </label>
        <input
          :value="model.sendLimitTotal"
          type="number"
          min="1"
          class="w-full px-4 py-2 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 placeholder-n-slate-9 focus:border-n-brand focus:outline-none"
          :placeholder="t('CAMPAIGN.V2.WIZARD.SCHEDULING.SEND_LIMIT_TOTAL_PLACEHOLDER')"
          @input="updateField('sendLimitTotal', $event.target.value ? parseInt($event.target.value) : null)"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ t('CAMPAIGN.V2.WIZARD.SCHEDULING.SEND_LIMIT_PER_RUN_LABEL') }}
        </label>
        <input
          :value="model.sendLimitPerRun"
          type="number"
          min="1"
          class="w-full px-4 py-2 text-sm border rounded-lg border-n-weak bg-n-surface-1 text-n-slate-12 placeholder-n-slate-9 focus:border-n-brand focus:outline-none"
          :placeholder="t('CAMPAIGN.V2.WIZARD.SCHEDULING.SEND_LIMIT_PER_RUN_PLACEHOLDER')"
          @input="updateField('sendLimitPerRun', $event.target.value ? parseInt($event.target.value) : null)"
        />
      </div>
    </div>
  </div>
</template>
